import React, { useState } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button } from '@chakra-ui/react';
import { useAuth } from '../../stores/auth/authStore';
import { useCalc } from '../../stores/calc/calcStore';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { config } from '../../config';

/**
 * Shown when another user's Firebase state was loaded for review (read-only).
 * Restoring reloads the signed-in user's cloud state and re-enables sync.
 */
const ReviewRemoteDataBanner: React.FC = () => {
    const { user } = useAuth();
    const { reviewSourceUid, fetchData } = useCalc();
    const [restoring, setRestoring] = useState(false);

    if (!reviewSourceUid || !user?.uid) return null;

    const handleRestore = async () => {
        try {
            setRestoring(true);
            await fetchData(user.uid, { suppressSuccessToast: true });
            showSuccessToast('Your data restored; saving is on again');
        } catch (e) {
            console.error(e);
            showErrorToast('Could not restore your data');
        } finally {
            setRestoring(false);
        }
    };

    return (
        <Box mb={3}>
            <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon alignSelf="flex-start" mt={0.5} />
                <Box flex="1" minW={0}>
                    <AlertTitle fontSize="sm">Read-only preview</AlertTitle>
                    <AlertDescription fontSize="xs" color="gray.700">
                        Viewing Firebase data for user <strong>{reviewSourceUid}</strong>. Changes are stored locally in this
                        browser only and are never synced to Firebase until you restore your account.
                    </AlertDescription>
                    <Button
                        mt={2}
                        size="sm"
                        bg={config.test5}
                        color="white"
                        _hover={{ bg: config.test4 }}
                        isLoading={restoring}
                        loadingText="Restoring"
                        onClick={handleRestore}
                    >
                        Restore my data
                    </Button>
                </Box>
            </Alert>
        </Box>
    );
};

export default ReviewRemoteDataBanner;
